import type { ReactNode } from 'react';
import type { PeerSearchResult, PlaceSnapshot } from '@/types/gmb';
import { formatNumber } from '@/pages/gmb-audit/utils/format';
import { targetRating, targetReviewCount } from '@/pages/gmb-audit/utils/peerRanking';

// UI-2B｜Benchmark Section「你和附近同業差在哪裡？」（純顯示層）。
// 只讀 targetSnapshot + peerComparison，不呼叫 Google、不重算中位數。
// 中位數一律以 Backend 統計為準（ratingMedian / reviewCountMedian）。

function BenchmarkRow({
  label,
  value,
  pct,
  tone,
}: {
  label: string;
  value: string;
  pct: number;
  tone: 'target' | 'median';
}) {
  const fill = tone === 'target' ? 'bg-primary-600' : 'bg-secondary-400';
  return (
    <div>
      <div className="flex items-center justify-between text-sm">
        <span className="text-foreground-600">{label}</span>
        <span className="font-bold text-foreground-950">{value}</span>
      </div>
      <div className="mt-1.5 h-2.5 w-full overflow-hidden rounded-full bg-background-200">
        <div className={`h-full rounded-full ${fill}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function CardShell({
  icon,
  title,
  children,
}: {
  icon: string;
  title: string;
  children: ReactNode;
}) {
  return (
    <div className="rounded-2xl border border-background-300 bg-background-50 p-5 shadow-card md:p-6">
      <div className="flex items-center gap-2.5">
        <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-50 text-primary-700">
          <i className={`${icon} text-lg`} />
        </span>
        <h3 className="text-lg text-foreground-950">{title}</h3>
      </div>
      {children}
    </div>
  );
}

function RatingBenchmarkCard({ target, peerComparison }: { target: PlaceSnapshot; peerComparison: PeerSearchResult }) {
  const mine = targetRating(target);
  const median = typeof peerComparison.ratingMedian === 'number' ? peerComparison.ratingMedian : null;
  const sample = peerComparison.ratingSampleCount;

  const scale = 5;
  const minePct = mine !== null ? Math.round((mine / scale) * 100) : 0;
  const medianPct = median !== null ? Math.round((median / scale) * 100) : 0;

  let diffText = '—';
  let conclusion = '尚無可用評分資料';
  if (mine !== null && median !== null) {
    const diff = mine - median;
    diffText = `${diff > 0 ? '+' : ''}${diff.toFixed(1)}`;
    if (diff < -0.05) conclusion = '評分略低於同業中位數。';
    else if (diff > 0.05) conclusion = '評分高於同業中位數。';
    else conclusion = '評分與同業中位數相當。';
  } else if (mine !== null && median === null) {
    conclusion = '同業評分樣本不足，暫無法比較。';
  }

  return (
    <CardShell icon="ri-star-line" title="Google 評分">
      <div className="mt-4 grid grid-cols-2 gap-3">
        <div>
          <p className="text-sm text-foreground-500">你</p>
          <p className="mt-0.5 text-3xl font-bold leading-none text-foreground-950">
            {mine !== null ? mine.toFixed(1) : '—'}
          </p>
        </div>
        <div>
          <p className="text-sm text-foreground-500">同業中位數</p>
          <p className="mt-0.5 text-3xl font-bold leading-none text-foreground-950">
            {median !== null ? median.toFixed(1) : '—'}
          </p>
        </div>
      </div>

      <div className="mt-4 space-y-2.5">
        <BenchmarkRow
          label="你"
          value={mine !== null ? mine.toFixed(1) : '尚無可用評分資料'}
          pct={minePct}
          tone="target"
        />
        <BenchmarkRow
          label="同業中位數"
          value={median !== null ? median.toFixed(1) : '同業評分樣本不足'}
          pct={medianPct}
          tone="median"
        />
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-background-200 pt-3.5">
        <span className="inline-flex items-center gap-1.5 text-sm text-foreground-500">
          差距
          <span className="text-base font-bold text-foreground-950">{diffText}</span>
        </span>
        <span className="text-sm text-foreground-600">{conclusion}</span>
      </div>

      <p className="mt-2 text-xs text-foreground-400">
        同業有效評分樣本 {typeof sample === 'number' ? `${sample} 家` : '不明'}
      </p>
    </CardShell>
  );
}

function ReviewBenchmarkCard({ target, peerComparison }: { target: PlaceSnapshot; peerComparison: PeerSearchResult }) {
  const mine = targetReviewCount(target);
  const median = typeof peerComparison.reviewCountMedian === 'number' ? peerComparison.reviewCountMedian : null;
  const sample = peerComparison.reviewCountSampleCount;

  const scale = Math.max(mine ?? 0, median ?? 0);
  const minePct = mine !== null && scale > 0 ? Math.round((mine / scale) * 100) : 0;
  const medianPct = median !== null && scale > 0 ? Math.round((median / scale) * 100) : 0;

  let diffText = '—';
  let ratioText = '—';
  let conclusion = '尚無可用評論數資料';
  if (mine !== null && median !== null) {
    const diff = mine - median;
    diffText = `${diff > 0 ? '+' : ''}${formatNumber(diff)}`;
    if (median > 0) {
      ratioText = `${Math.round((mine / median) * 100)}%`;
    }
    if (median > 0 && mine / median < 0.5) conclusion = '評論累積明顯少於同業。';
    else if (diff < 0) conclusion = '評論數略低於同業中位數。';
    else if (diff > 0) conclusion = '評論數高於同業中位數。';
    else conclusion = '評論數與同業中位數相當。';
  } else if (mine !== null && median === null) {
    conclusion = '同業評論樣本不足，暫無法比較。';
  }

  return (
    <CardShell icon="ri-chat-3-line" title="評論數">
      <div className="mt-4 grid grid-cols-2 gap-3">
        <div>
          <p className="text-sm text-foreground-500">你</p>
          <p className="mt-0.5 text-3xl font-bold leading-none text-foreground-950">
            {mine !== null ? formatNumber(mine) : '—'}
          </p>
        </div>
        <div>
          <p className="text-sm text-foreground-500">同業中位數</p>
          <p className="mt-0.5 text-3xl font-bold leading-none text-foreground-950">
            {median !== null ? formatNumber(median) : '—'}
          </p>
        </div>
      </div>

      <div className="mt-4 space-y-2.5">
        <BenchmarkRow
          label="你"
          value={mine !== null ? formatNumber(mine) : '資料不足'}
          pct={minePct}
          tone="target"
        />
        <BenchmarkRow
          label="同業中位數"
          value={median !== null ? formatNumber(median) : '同業評論樣本不足'}
          pct={medianPct}
          tone="median"
        />
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-background-200 pt-3.5">
        <span className="inline-flex items-center gap-1.5 text-sm text-foreground-500">
          差距
          <span className="text-base font-bold text-foreground-950">{diffText}</span>
        </span>
        {ratioText !== '—' && (
          <span className="inline-flex items-center gap-1.5 text-sm text-foreground-500">
            約為同業中位數
            <span className="text-base font-bold text-foreground-950">{ratioText}</span>
          </span>
        )}
      </div>
      <p className="mt-2 text-sm text-foreground-600">{conclusion}</p>

      <p className="mt-2 text-xs text-foreground-400">
        同業有效評論樣本 {typeof sample === 'number' ? `${sample} 家` : '不明'}
      </p>
    </CardShell>
  );
}

export default function ReportBenchmarkSection({
  target,
  peerComparison,
}: {
  target: PlaceSnapshot;
  peerComparison?: PeerSearchResult;
}) {
  if (!peerComparison) return null;

  return (
    <section className="ds-container pt-12 md:pt-16">
      <div className="mb-3 flex items-center gap-3">
        <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary-50 text-primary-700">
          <i className="ri-bar-chart-grouped-line text-xl" />
        </span>
        <h2 className="text-xl md:text-2xl">你和附近同業差在哪裡？</h2>
      </div>
      <p className="mb-6 text-base text-foreground-500">
        以下比較來自本次選取的同業樣本，不是 Google 官方搜尋排名。
      </p>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <RatingBenchmarkCard target={target} peerComparison={peerComparison} />
        <ReviewBenchmarkCard target={target} peerComparison={peerComparison} />
      </div>
    </section>
  );
}